import NextImage from 'next/image';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { Paragraph } from '@/core/components/Typography';
import { useDecodedCategories } from '@/core/hooks/useDecodedCategories';
import { mapCategories } from '@/core/constants/reports';
import { getCategoriesSelector } from '@/core/store/reducers/config';

import type { AnalysisDetail } from '@/features/ChatBot/interfaces';
import {
  getEventDateDisplay,
  getEventTimeDisplay,
} from '@/features/ChatBot/utils/formatAnalysisMeta';
import { isQuillHtmlDescription } from '@/features/ChatBot/utils/isQuillHtmlDescription';

import { MarkdownText } from '../ChatContent/MarkdownText';
import {
  ContentCard,
  DescriptionBlock,
  DescriptionBlockHtml,
  DescriptionSection,
  MetadataPill,
  MetadataPillIconWrap,
  MetadataSection,
  MetadataValuePills,
} from './AnalysisViewModal.styles';
import { MetadataFieldItem } from './MetadataFieldItem';

export type AnalysisMetadataContentProps = {
  analysis: AnalysisDetail;
};

export function AnalysisMetadataContent({ analysis }: AnalysisMetadataContentProps) {
  const { colors } = useTheme();
  const categoriesList = useSelector(getCategoriesSelector);

  const meta = analysis.meta;
  const eventDate = meta?.eventDate;
  const eventTime = meta?.eventTime;
  const eventEndDate = meta?.eventEndDate;
  const eventEndTime = meta?.eventEndTime;
  const impact = meta?.impact;

  const eventDateDisplay = getEventDateDisplay(eventDate, eventEndDate);
  const eventTimeDisplay = getEventTimeDisplay(eventTime, eventEndTime);

  const locationDisplay = analysis.location ?? meta?.locations?.[0]?.address ?? '';
  const countryDisplay = meta?.countries?.[0]?.name;
  const categoryNames = useMemo(
    () => meta?.categories?.map(category => category.name) ?? [],
    [meta?.categories]
  );
  const decodedCategoryNames = useDecodedCategories(categoryNames);
  const reportDisplay = meta?.reportType;
  const impactColors = impact ? colors.impact[impact] : null;
  const descriptionContent = analysis.description ?? null;

  const isHtmlDescription = Boolean(
    descriptionContent && isQuillHtmlDescription(descriptionContent)
  );

  const categoryPills = useMemo(() => {
    return decodedCategoryNames.reduce<Array<React.ReactElement>>((acc, decodedName, i) => {
      const categoryName = categoryNames[i];

      if (!categoryName) return acc;

      const categoryItem = categoriesList.find(c => c.name === categoryName);
      const categoryData = mapCategories(decodedName, 12, colors.categoryIcon);

      acc.push(
        <MetadataPill
          key={`${decodedName}-${i}`}
          $backgroundColor={categoryData.color ?? undefined}
        >
          {categoryItem?.subCategorySvg ? (
            <MetadataPillIconWrap>
              <NextImage
                src={categoryItem.subCategorySvg}
                width={15}
                height={15}
                alt={decodedName}
              />
            </MetadataPillIconWrap>
          ) : categoryData.icon ? (
            <MetadataPillIconWrap>{categoryData.icon}</MetadataPillIconWrap>
          ) : null}
          <Paragraph type="tiny">{decodedName}</Paragraph>
        </MetadataPill>
      );

      return acc;
    }, []);
  }, [decodedCategoryNames, categoryNames, categoriesList, colors.categoryIcon]);

  return (
    <ContentCard>
      <MetadataSection>
        <MetadataFieldItem label="Location">{locationDisplay}</MetadataFieldItem>
        <MetadataFieldItem label="Country">{countryDisplay}</MetadataFieldItem>
        <MetadataFieldItem label="Event date">{eventDateDisplay}</MetadataFieldItem>
        <MetadataFieldItem label="Event time">{eventTimeDisplay}</MetadataFieldItem>
        <MetadataFieldItem label="Report">{reportDisplay}</MetadataFieldItem>
        <MetadataFieldItem
          label="Impact"
          variant="pill"
        >
          {impact ? (
            <MetadataValuePills>
              <MetadataPill
                $backgroundColor={impactColors?.backgroundColor}
                $textColor={impactColors?.textColor}
              >
                <Paragraph type="tiny">{impact}</Paragraph>
              </MetadataPill>
            </MetadataValuePills>
          ) : null}
        </MetadataFieldItem>
        <MetadataFieldItem
          label="Category"
          variant="pill"
        >
          {categoryPills.length > 0 ? (
            <MetadataValuePills>{categoryPills}</MetadataValuePills>
          ) : null}
        </MetadataFieldItem>
      </MetadataSection>
      {descriptionContent && (
        <DescriptionSection>
          {isHtmlDescription ? (
            <DescriptionBlockHtml dangerouslySetInnerHTML={{ __html: descriptionContent }} />
          ) : (
            <DescriptionBlock>
              <MarkdownText
                text={descriptionContent}
                paragraphType="normal"
              />
            </DescriptionBlock>
          )}
        </DescriptionSection>
      )}
    </ContentCard>
  );
}
